import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import {
  Public as PublicIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Smartphone as DeviceIcon
} from '@mui/icons-material';
// import { startOfDay, endOfDay } from 'date-fns';
import { useErrorSetter } from '../hooks/useUiMessages';
import { fetchGeographicAnalytics } from './analyticsApi';
import styles from './Analytics.module.css';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f', '#8dd1e1', '#d084d0'];

const GeographicInsights = ({ dateRange }) => {
  const setError = useErrorSetter();
  const [loading, setLoading] = useState(true);
  const [geographicData, setGeographicData] = useState({
    countryData: [],
    cityData: [],
    regionData: [],
    languageData: [],
    timezoneData: [],
    deviceByCountry: [],
    trafficSources: [],
    conversionByCountry: [],
    summary: {
      totalCountries: 0,
      topCountry: '',
      topCountryPercentage: 0,
      avgSessionByCountry: 0,
      internationalTraffic: 0,
      primaryLanguage: '',
      primaryTimezone: ''
    }
  });

  const fetchGeographicData = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
      if (!token) {
        setGeographicData(prev => ({ ...prev, countryData: [], languageData: [], summary: { totalCountries:0, topCountry:'', topCountryPercentage:0 } }));
        return;
      }
      const raw = await fetchGeographicAnalytics(
        dateRange?.start ? new Date(dateRange.start).toISOString() : undefined,
        dateRange?.end ? new Date(dateRange.end).toISOString() : undefined
      );
      const totalSessions = (raw.countryData || []).reduce((s,c)=> s + (c.sessions||0),0) || 1;
      setGeographicData(prev => ({
        ...prev,
        countryData: (raw.countryData || []).map(c => ({ ...c, percentage: totalSessions ? parseFloat(((c.sessions/ totalSessions)*100).toFixed(1)) : 0, avgSessionsPerUser: c.users ? (c.sessions / c.users).toFixed(1) : '0.0' })),
        regionData: (raw.regionData || []).map(r => ({ ...r })),
        cityData: (raw.cityData || []).map(c => ({ ...c })),
        languageData: (raw.languageData || []).map((l,i)=> ({ ...l, color: COLORS[i % COLORS.length] })),
        timezoneData: (raw.timezoneData || []).map((t,i)=> ({ ...t, color: COLORS[i % COLORS.length] })),
        deviceByCountry: raw.deviceByCountry || [],
        conversionByCountry: raw.conversionByCountry || [],
        summary: {
          totalCountries: raw.summary?.totalCountries || 0,
          topCountry: raw.summary?.topCountry || '',
            topCountryPercentage: raw.summary?.topCountryPercentage || 0,
          avgSessionByCountry: raw.summary?.avgSessionByCountry || 0,
          internationalTraffic: raw.summary?.internationalTraffic || 0,
          primaryLanguage: raw.summary?.primaryLanguage || ((raw.languageData && raw.languageData[0]?.language) || ''),
          primaryTimezone: raw.summary?.primaryTimezone || ((raw.timezoneData && raw.timezoneData[0]?.timezone) || '')
        }
      }));
    } catch (err) {
      console.error('Error fetching geographic data:', err);
      setError('Failed to load geographic metrics');
    } finally {
      setLoading(false);
    }
  }, [setError, dateRange]);

  useEffect(() => {
    fetchGeographicData();
  }, [fetchGeographicData]);

  // Removed legacy processors – backend supplies datasets.

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading geographic data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className={styles.geographicContainer}>
      {/* Summary Cards */}
      <Grid container spacing={3} className={styles.metricsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <PublicIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Total Countries
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {geographicData.summary.totalCountries}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <LocationIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Top Country
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {geographicData.summary.topCountry}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formatPercentage(geographicData.summary.topCountryPercentage)} of traffic
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <LanguageIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Primary Language
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {geographicData.summary.primaryLanguage}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <DeviceIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  International Traffic
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {formatPercentage(geographicData.summary.internationalTraffic)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={3} className={styles.chartsGrid}>
        {/* Top Countries */}
        <Grid item xs={12} lg={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Traffic by Country
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={geographicData.countryData.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'users' ? `${formatNumber(value)} users` : `${formatNumber(value)} sessions`,
                    name === 'users' ? 'Users' : 'Sessions'
                  ]} />
                  <Legend />
                  <Bar dataKey="users" fill={COLORS[0]} name="Users" />
                  <Bar dataKey="sessions" fill={COLORS[1]} name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Language Distribution */}
        <Grid item xs={12} lg={4}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Language Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={geographicData.languageData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="percentage"
                    label={({ language, percentage }) => `${language}: ${percentage}%`}
                  >
                    {geographicData.languageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Regional Distribution */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Traffic by Region
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={geographicData.regionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="region" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${formatNumber(value)}`, 'Users']} />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    stroke={COLORS[2]} 
                    fill={COLORS[2]}
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Device Usage by Country */}
        <Grid item xs={12} md={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Device Usage by Country
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={geographicData.deviceByCountry}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="country" angle={-45} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}%`, 'Usage']} />
                  <Legend />
                  <Bar dataKey="mobile" stackId="a" fill={COLORS[3]} name="Mobile" />
                  <Bar dataKey="desktop" stackId="a" fill={COLORS[4]} name="Desktop" />
                  <Bar dataKey="tablet" stackId="a" fill={COLORS[5]} name="Tablet" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Cities Table */}
        <Grid item xs={12} md={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Top Cities by Traffic
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>City</TableCell>
                      <TableCell align="right">Users</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Avg Sessions/User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {geographicData.cityData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell align="right">{formatNumber(row.users)}</TableCell>
                        <TableCell align="right">{formatNumber(row.sessions)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={row.avgSessionsPerUser}
                            color={parseFloat(row.avgSessionsPerUser) > 3 ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Timezone Distribution */}
        <Grid item xs={12} md={4}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Timezone Distribution
              </Typography>
              <List className={styles.timezoneList}>
                {geographicData.timezoneData.map((tz, index) => (
                  <ListItem key={index} className={styles.timezoneItem}>
                    <ListItemText
                      primary={tz.timezone}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="primary">
                            {formatNumber(tz.users)} users ({tz.percentage}%)
                          </Typography>
                          <Box className={styles.timezoneProgress}>
                            <Box 
                              className={styles.timezoneBar}
                              style={{ 
                                width: `${tz.percentage}%`,
                                backgroundColor: COLORS[index % COLORS.length]
                              }}
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Conversion by Country */}
        <Grid item xs={12}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Conversion Rate by Country
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={geographicData.conversionByCountry}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="country" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'conversionRate' ? `${value}%` : `$${value}`,
                      name === 'conversionRate' ? 'Conversion Rate' : 'Revenue'
                    ]}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="conversionRate" 
                    stroke={COLORS[6]} 
                    strokeWidth={3}
                    name="Conversion Rate (%)"
                  />
                  <Bar yAxisId="right" dataKey="revenue" fill={COLORS[7]} name="Revenue ($)" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default GeographicInsights;
